const PAYCHANGU_JQUERY = "https://code.jquery.com/jquery-3.7.1.min.js";
const PAYCHANGU_POPUP_SCRIPT = "https://in.paychangu.com/js/popup.js";
const PAYCHANGU_WRAPPER_ID = "wrapper";

let depsPromise = null;

function loadScript(src, id) {
    return new Promise((resolve, reject) => {
        if (id && document.getElementById(id)) {
            resolve();
            return;
        }

        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
            if (existing.getAttribute("data-loaded") === "true") {
                resolve();
                return;
            }
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener("error", () => reject(new Error(`Could not load ${src}`)), {
                once: true,
            });
            return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        if (id) script.id = id;
        script.onload = () => {
            script.setAttribute("data-loaded", "true");
            resolve();
        };
        script.onerror = () => reject(new Error(`Could not load ${src}`));
        document.body.appendChild(script);
    });
}

/** PayChangu popup.js expects jQuery and a #wrapper mount point (see PayChangu inline docs). */
export function ensurePaychanguMountPoint() {
    if (typeof document === "undefined") return;

    let wrapper = document.getElementById(PAYCHANGU_WRAPPER_ID);
    if (!wrapper) {
        wrapper = document.createElement("div");
        wrapper.id = PAYCHANGU_WRAPPER_ID;
        wrapper.setAttribute("aria-hidden", "true");
        document.body.appendChild(wrapper);
    }
}

export function loadPaychanguPopupScript() {
    if (typeof window !== "undefined" && typeof window.PaychanguCheckout === "function") {
        ensurePaychanguMountPoint();
        return Promise.resolve();
    }

    if (depsPromise) {
        return depsPromise;
    }

    depsPromise = (async () => {
        ensurePaychanguMountPoint();

        if (typeof window.jQuery === "undefined" && typeof window.$ === "undefined") {
            await loadScript(PAYCHANGU_JQUERY, "paychangu-jquery");
        }

        await loadScript(PAYCHANGU_POPUP_SCRIPT, "paychangu-popup");

        if (typeof window.PaychanguCheckout !== "function") {
            throw new Error("PayChangu checkout script did not initialize");
        }
    })().catch((err) => {
        depsPromise = null;
        throw err;
    });

    return depsPromise;
}

/**
 * Opens PayChangu inline popup checkout on the current page.
 * @see https://developer.paychangu.com/docs/inline-popup
 */
export async function openPaychanguInlineCheckout(session) {
    const publicKey = String(session?.publicKey || "").trim();
    const txRef = String(session?.txRef || session?.referenceNumber || "").trim();
    const amount = Number(session?.amount);

    if (!publicKey) {
        throw new Error(
            "PayChangu public key is missing. Set PAYCHANGU_PUBLIC_KEY in Supabase secrets or REACT_APP_PAYCHANGU_PUBLIC_KEY on Netlify.",
        );
    }
    if (!txRef) {
        throw new Error("Payment reference is missing. Try again.");
    }
    if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error("Payment amount is invalid. Try again.");
    }

    await loadPaychanguPopupScript();
    ensurePaychanguMountPoint();

    if (typeof window.PaychanguCheckout !== "function") {
        throw new Error("PayChangu checkout is unavailable. Refresh the page and try again.");
    }

    const customer = session.customer || {};
    const customization = session.customization || {};

    window.PaychanguCheckout({
        public_key: publicKey,
        tx_ref: txRef,
        amount,
        currency: session.currency || "MWK",
        callback_url: session.callbackUrl,
        return_url: session.returnUrl,
        customer: {
            email: customer.email || "",
            first_name: customer.first_name || customer.firstName || "School",
            last_name: customer.last_name || customer.lastName || "Admin",
        },
        customization: {
            title: customization.title || "Subscription payment",
            description: customization.description || "School subscription",
        },
        meta: session.meta || {},
    });
}
